// =========================================================================
// TRON transaction decoding for the co-sign screen (blueprint §8).
//
// THIS MODULE IS THE ENFORCEMENT. TRON's permission system scopes by contract
// TYPE only — an active key authorised for TriggerSmartContract can call ANY
// contract with ANY calldata. So "this payout is 40 USDT to Al Marwan" is not
// something the chain can guarantee; it is something we must PROVE from the
// raw transaction bytes and show to a human before they touch their Ledger.
//
// WHAT IS AUTHORITATIVE: `raw_data_hex` — the protobuf bytes. The signature is
// over sha256 of those bytes, and the Ledger renders those bytes. The node's
// `raw_data` JSON is a courtesy view an attacker can forge freely, so decoding
// it proves nothing. Everything here decodes the wire format; the JSON, when
// present, is cross-checked and any disagreement is treated as forgery.
//
// WHAT THIS CANNOT DO: if the same compromised server supplies both the
// transaction AND the expectations it is checked against, this only proves the
// server agrees with itself. The recipient must be anchored to something the
// server does not control — the operator reading it off the Ledger screen, and
// a whitelist the client trusts independently. See assertCosignSafe.
//
// Everything throws loudly. There is no "probably fine" path: if this module
// cannot prove exactly what a transaction does, the operator must not sign it.
// =========================================================================

import { sha256 } from '@noble/hashes/sha256';
import { decodeTronAddress, encodeTronAddress, TRON_ADDRESS_PREFIX, assertPlausibleAddress } from './address.mjs';
import { WIRE, parseStrict, one, many, int32, int64, text } from './protobuf.mjs';
import { printable } from './printable.mjs';

// ---- known contracts & selectors ----------------------------------------

// USDT-TRC20 on TRON mainnet. Hardcoded, not configurable at runtime: an
// attacker-supplied "token address" is exactly the attack this prevents. It
// also stops address-poisoning dust from ever triggering a sweep.
export const USDT_TRC20_MAINNET = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';
// Nile testnet USDT, for the pre-flight rehearsal.
export const USDT_TRC20_NILE = 'TXYZopYRdj2D9XRtbG411XZZ3kM5VkAeBf';

export const SELECTORS = Object.freeze({
  transfer: 'a9059cbb',      // transfer(address,uint256)      — the ONLY one we sign
  approve: '095ea7b3',       // approve(address,uint256)       — must be refused
  transferFrom: '23b872dd',  // transferFrom(address,address,uint256)
  increaseAllowance: '39509351',
});

const SELECTOR_NAMES = Object.fromEntries(
  Object.entries(SELECTORS).map(([k, v]) => [v, k]),
);

// A TRC-20 transfer call is exactly: 4-byte selector + 32-byte address + 32-byte amount.
const TRANSFER_CALLDATA_BYTES = 4 + 32 + 32;
const UINT256_MAX = (1n << 256n) - 1n;

// ---- hex helpers ---------------------------------------------------------

// A payout transaction is a couple of hundred bytes. Bounding input up front
// stops a hostile server freezing an operator's phone with a huge payload
// before any structural check gets a chance to reject it.
const MAX_HEX_INPUT_BYTES = 16 * 1024;

function stripHex(h) {
  if (typeof h !== 'string') throw new Error('expected a hex string');
  return h.startsWith('0x') || h.startsWith('0X') ? h.slice(2) : h;
}

function hexToBytes(h) {
  const s = stripHex(h);
  if (s.length > MAX_HEX_INPUT_BYTES * 2) {
    throw new Error(`hex input exceeds ${MAX_HEX_INPUT_BYTES} bytes — refusing to parse`);
  }
  if (s.length % 2 !== 0) throw new Error('hex string has odd length');
  if (!/^[0-9a-fA-F]*$/.test(s)) throw new Error('hex string contains non-hex characters');
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}


function bytesToHex(b) {
  return [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

function bytesToBigInt(b) {
  let v = 0n;
  for (const byte of b) v = (v << 8n) | BigInt(byte);
  return v;
}

// ---- address conversion --------------------------------------------------

// A 41-prefixed hex address (as it appears in raw_data) → base58check.
export function hexAddressToBase58(hexAddr) {
  const b = hexToBytes(hexAddr);
  if (b.length !== 21) throw new Error(`address must be 21 bytes, got ${b.length}`);
  if (b[0] !== TRON_ADDRESS_PREFIX) throw new Error('address does not start with 0x41');
  return encodeTronAddress(b);
}

export function base58ToHexAddress(base58) {
  return bytesToHex(decodeTronAddress(base58));
}

// ---- TRC-20 calldata -----------------------------------------------------

// Encode transfer(address,uint256). Address is the 20-byte body WITHOUT the
// 0x41 prefix, left-padded to 32 bytes — the EVM ABI convention TRON inherits.
export function encodeTrc20Transfer(toBase58, amountBaseUnits) {
  if (typeof amountBaseUnits !== 'bigint') throw new Error('amount must be a bigint');
  if (amountBaseUnits <= 0n) throw new Error('amount must be positive');
  if (amountBaseUnits > UINT256_MAX) throw new Error('amount exceeds uint256');
  assertPlausibleAddress(toBase58, 'recipient');
  const body = decodeTronAddress(toBase58);           // validates checksum
  const addr = bytesToHex(body.slice(1)).padStart(64, '0');
  const amt = amountBaseUnits.toString(16).padStart(64, '0');
  return SELECTORS.transfer + addr + amt;
}

// Decode transfer(address,uint256) — and ONLY that.
//
// Every rejection below corresponds to a real way a malicious payload could
// look legitimate to a careless parser.
export function decodeTrc20Transfer(dataHex) {
  const data = hexToBytes(dataHex);

  if (data.length === 0) throw new Error('empty calldata — not a token transfer');

  const selector = bytesToHex(data.slice(0, 4));
  if (selector !== SELECTORS.transfer) {
    const named = SELECTOR_NAMES[selector];
    throw new Error(
      named
        ? `refusing ${named}() — only transfer() may be co-signed`
        : `unknown method selector 0x${selector} — refusing to sign an unrecognised call`,
    );
  }

  // Length must be EXACT. Trailing bytes are a classic way to smuggle data
  // past a parser that only reads the prefix it expects.
  if (data.length !== TRANSFER_CALLDATA_BYTES) {
    throw new Error(
      `transfer() calldata must be exactly ${TRANSFER_CALLDATA_BYTES} bytes, got ${data.length}`,
    );
  }

  // The address parameter is 32 bytes but only the low 20 are the address.
  // Non-zero padding means a malformed encoding that other decoders may render
  // differently — refuse rather than guess.
  const addrWord = data.slice(4, 36);
  for (let i = 0; i < 12; i++) {
    if (addrWord[i] !== 0) {
      throw new Error('recipient address has non-zero padding — malformed or deliberately ambiguous');
    }
  }
  const addr20 = addrWord.slice(12);
  if (addr20.every((b) => b === 0)) throw new Error('recipient is the zero address');

  const body = new Uint8Array(21);
  body[0] = TRON_ADDRESS_PREFIX;
  body.set(addr20, 1);

  const amount = bytesToBigInt(data.slice(36, 68));
  if (amount === 0n) throw new Error('transfer amount is zero');

  return { to: encodeTronAddress(body), amount };
}

// ---- transaction-level decode, FROM THE SIGNED BYTES ---------------------
//
// Critical: the signature and the Ledger's display both cover raw_data_hex —
// the protobuf bytes. `raw_data` (the JSON) is a courtesy rendering by the node
// and is NOT what gets signed. Validating the JSON proves nothing: a malicious
// server can serve benign JSON alongside hostile bytes, and the operator would
// approve the bytes. So everything below decodes the wire format and treats the
// JSON, if present, as an untrusted claim to be checked against the bytes.

// Every level is read against TRON's schema: a field the schema does not have,
// a known field with the wrong wire type, a singular field written twice, a
// number outside its type — each refused, because each is something
// protobuf-java could read differently from us (review findings A2, B1).
// Fields TRON has but no transfer this desk builds carries are refused by
// name (A10): a memo above all, which the Ledger, this screen and the fee
// limit all leave out of the picture, and which the chain charges for.
const { VARINT, LEN } = WIRE;
const NEVER_BUILT = 'neither a node nor this desk writes it into a token transfer';

// Transaction.raw
const RAW_F = { REF_BLOCK_BYTES: 1, REF_BLOCK_HASH: 4, EXPIRATION: 8, CONTRACT: 11, TIMESTAMP: 14, FEE_LIMIT: 18 };
const RAW_SCHEMA = {
  1: { name: 'ref_block_bytes', wire: LEN },
  3: { name: 'ref_block_num', refuse: NEVER_BUILT },
  4: { name: 'ref_block_hash', wire: LEN },
  8: { name: 'expiration', wire: VARINT },
  9: { name: 'auths', refuse: NEVER_BUILT },
  10: { name: 'data', refuse: 'a memo — no payout carries one, the co-sign screen would not show it, and the chain charges the sender for it' },
  11: { name: 'contract', wire: LEN, repeated: true },
  12: { name: 'scripts', refuse: NEVER_BUILT },
  14: { name: 'timestamp', wire: VARINT },
  18: { name: 'fee_limit', wire: VARINT },
};
// Transaction.Contract
const C_F = { TYPE: 1, PARAMETER: 2, PERMISSION_ID: 5 };
const CONTRACT_SCHEMA = {
  1: { name: 'type', wire: VARINT },
  2: { name: 'parameter', wire: LEN },
  3: { name: 'provider', refuse: NEVER_BUILT },
  4: { name: 'ContractName', refuse: NEVER_BUILT },
  5: { name: 'Permission_id', wire: VARINT },
};
// google.protobuf.Any
const ANY_F = { TYPE_URL: 1, VALUE: 2 };
const ANY_SCHEMA = { 1: { name: 'type_url', wire: LEN }, 2: { name: 'value', wire: LEN } };
// TriggerSmartContract
const TSC_F = { OWNER: 1, CONTRACT: 2, CALL_VALUE: 3, DATA: 4, CALL_TOKEN_VALUE: 5, TOKEN_ID: 6 };
const TSC_SCHEMA = {
  1: { name: 'owner_address', wire: LEN },
  2: { name: 'contract_address', wire: LEN },
  3: { name: 'call_value', wire: VARINT },
  4: { name: 'data', wire: LEN },
  5: { name: 'call_token_value', wire: VARINT },
  6: { name: 'token_id', wire: VARINT },
};

export const CONTRACT_TYPE_TRIGGER_SMART_CONTRACT = 31;
export const TYPE_URL_TRIGGER = 'type.googleapis.com/protocol.TriggerSmartContract';

// A payout needs far less than this; anything more is fee-burn griefing.
export const MAX_REASONABLE_FEE_LIMIT_SUN = 100_000_000n; // 100 TRX

// Co-signing windows are deliberately much tighter than the 24h protocol max:
// a long window lets an attacker harvest a signature now and broadcast later,
// after the desk has been refilled or the payout "cancelled".
export const MAX_COSIGN_WINDOW_MS = 60 * 60 * 1000; // 1 hour

// An epoch-millisecond field past this is no date a node accepts, and would
// not survive conversion to a Number intact.
function epochMs(fields, n, what) {
  const v = int64(fields, n, what);
  if (v > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error(`${what} is ${v} — past any date a node accepts`);
  return Number(v);
}

/**
 * Decode the bytes that will actually be signed. This is the authoritative
 * decode; nothing else in this module is trusted for a signing decision.
 */
export function decodeSignedBytes(rawDataHex) {
  const raw = hexToBytes(rawDataHex);
  if (raw.length === 0) throw new Error('raw_data_hex is empty');

  // The txID is sha256 over exactly these bytes — the same preimage the device
  // hashes, which is what makes comparing it on-screen meaningful.
  const txID = bytesToHex(sha256(raw));

  const rf = parseStrict(raw, RAW_SCHEMA, 'transaction');

  const contracts = many(rf, RAW_F.CONTRACT);
  if (contracts.length !== 1) {
    throw new Error(`expected exactly 1 contract in the signed bytes, got ${contracts.length}`);
  }

  const cf = parseStrict(contracts[0], CONTRACT_SCHEMA, 'contract');

  const typeId = int32(cf, C_F.TYPE, 'contract.type');
  if (typeId !== BigInt(CONTRACT_TYPE_TRIGGER_SMART_CONTRACT)) {
    throw new Error(
      `signed bytes are contract type ${typeId}, not TriggerSmartContract (${CONTRACT_TYPE_TRIGGER_SMART_CONTRACT}) — ` +
      'the co-sign flow signs token transfers only',
    );
  }

  const anyBuf = one(cf, C_F.PARAMETER);
  if (!anyBuf) throw new Error('contract has no parameter');
  const af = parseStrict(anyBuf, ANY_SCHEMA, 'parameter');
  const typeUrl = text(af, ANY_F.TYPE_URL, 'parameter.type_url');
  if (typeUrl !== TYPE_URL_TRIGGER) {
    throw new Error(`type_url is "${printable(typeUrl)}", which disagrees with the contract type — evidence of forgery`);
  }

  const inner = one(af, ANY_F.VALUE);
  if (!inner) throw new Error('parameter has no value');
  const tf = parseStrict(inner, TSC_SCHEMA, 'TriggerSmartContract');

  const callValue = int64(tf, TSC_F.CALL_VALUE, 'call_value');
  if (callValue !== 0n) throw new Error(`call_value is ${callValue} sun — this also moves TRX; refusing`);
  // TRC-10 can ride along on the same call and would be invisible in a token summary.
  const callTokenValue = int64(tf, TSC_F.CALL_TOKEN_VALUE, 'call_token_value');
  if (callTokenValue !== 0n) throw new Error(`call_token_value is ${callTokenValue} — a TRC-10 transfer is attached; refusing`);
  if (tf.has(TSC_F.TOKEN_ID)) throw new Error('token_id is present — a TRC-10 leg is attached; refusing');

  const ownerBytes = one(tf, TSC_F.OWNER);
  const contractBytes = one(tf, TSC_F.CONTRACT);
  const dataBytes = one(tf, TSC_F.DATA);
  if (!ownerBytes) throw new Error('missing owner_address');
  if (!contractBytes) throw new Error('missing contract_address');
  if (!dataBytes) throw new Error('missing calldata');

  const { to, amount } = decodeTrc20Transfer(bytesToHex(dataBytes));

  const owner = hexAddressToBase58(bytesToHex(ownerBytes));
  const contractAddr = hexAddressToBase58(bytesToHex(contractBytes));

  // Two burns that a zero-address check misses. Sending USDT to the token
  // contract itself is unrecoverable; sending to the spending wallet is a
  // no-op that still costs energy and would read as a completed payout.
  if (to === contractAddr) {
    throw new Error('recipient is the token contract itself — those funds would be unrecoverable');
  }
  if (to === owner) {
    throw new Error('recipient is the sending wallet — this is a no-op dressed as a payout');
  }

  const feeLimit = int64(rf, RAW_F.FEE_LIMIT, 'fee_limit');
  if (feeLimit > MAX_REASONABLE_FEE_LIMIT_SUN) {
    throw new Error(
      `fee_limit is ${feeLimit} sun (${Number(feeLimit) / 1e6} TRX) — far above what a token transfer needs; refusing`,
    );
  }

  const refBlockBytes = one(rf, RAW_F.REF_BLOCK_BYTES);
  const refBlockHash = one(rf, RAW_F.REF_BLOCK_HASH);
  // The reference block is two bytes of a block number and eight of its id
  // (tron-build.mjs, blockReference). Longer is no transaction a node takes —
  // and a field of hundreds of bytes would reach a Ledger on a cable as a first
  // command holding nothing but the key path (review of 6 October).
  if (refBlockBytes !== undefined && refBlockBytes.length !== 2) {
    throw new Error(`ref_block_bytes is ${refBlockBytes.length} bytes; a transaction carries 2`);
  }
  if (refBlockHash !== undefined && refBlockHash.length !== 8) {
    throw new Error(`ref_block_hash is ${refBlockHash.length} bytes; a transaction carries 8`);
  }

  return {
    txID,
    type: 'trc20_transfer',
    owner,
    contract: contractAddr,
    to,
    amount,
    callValue,
    feeLimit,
    permissionId: Number(int32(cf, C_F.PERMISSION_ID, 'contract.Permission_id')),
    expiration: epochMs(rf, RAW_F.EXPIRATION, 'expiration') || null,
    // The rest of what the bytes say — read by the JSON cross-check below.
    timestamp: epochMs(rf, RAW_F.TIMESTAMP, 'timestamp') || null,
    refBlockBytes: refBlockBytes ? bytesToHex(refBlockBytes) : null,
    refBlockHash: refBlockHash ? bytesToHex(refBlockHash) : null,
    typeUrl,
    calldata: bytesToHex(dataBytes),
  };
}

// The keys a node's JSON rendering of a token transfer has at each level.
const JSON_KEYS = {
  raw_data: ['contract', 'ref_block_bytes', 'ref_block_hash', 'expiration', 'timestamp', 'fee_limit'],
  contract: ['parameter', 'type', 'Permission_id'],
  parameter: ['value', 'type_url'],
  value: ['data', 'owner_address', 'contract_address', 'call_value', 'call_token_value', 'token_id'],
};

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Cross-check the node's JSON rendering against the authoritative bytes —
 * every field of it (review finding A7). Two things act on this JSON rather
 * than the bytes: TronWeb's multiSign, which TronLink runs before it asks a
 * Ledger for anything, rebuilds the bytes from it and refuses any difference;
 * and a node's /wallet/broadcasttransaction broadcasts what it says. So it
 * must say exactly what the bytes say and nothing more. A field the bytes
 * omit is its protobuf default, which the JSON may leave out or state.
 * Addresses are hex unless the transaction says `visible: true` (A6).
 *
 * Any disagreement is proof of forgery, not a warning — a legitimate node
 * always produces JSON that matches its own bytes.
 */
export function assertJsonMatchesBytes(tx, decoded) {
  const r = tx?.raw_data;
  if (r === undefined) return; // the JSON view is optional; the bytes are what count
  const mismatch = [];
  const keysOf = (obj, level) => {
    if (!isObject(obj)) { mismatch.push(`${level} is not an object`); return false; }
    for (const k of Object.keys(obj)) {
      if (!JSON_KEYS[level].includes(k)) mismatch.push(`${level}.${k}, which the bytes do not carry`);
    }
    return true;
  };
  // A number the JSON states, or the default the bytes imply by leaving it out.
  const whole = (v) => (v === undefined ? 0 : Number.isSafeInteger(v) && v >= 0 ? v : NaN);
  // Hex in either case is the same hex (review of 3 October); compared lower-case.
  const hexOrNull = (v) => (v === undefined ? null : typeof v === 'string' && /^[0-9a-fA-F]+$/.test(v) ? v.toLowerCase() : NaN);
  const address = (v) => {
    if (typeof v !== 'string') return null;
    try {
      if (tx.visible === true) {
        assertPlausibleAddress(v);
        return encodeTronAddress(decodeTronAddress(v));
      }
      return /^41[0-9a-fA-F]{40}$/.test(v) ? hexAddressToBase58(v) : null;
    } catch { return null; }
  };

  if (keysOf(r, 'raw_data')) {
    if (hexOrNull(r.ref_block_bytes) !== decoded.refBlockBytes) mismatch.push('ref_block_bytes');
    if (hexOrNull(r.ref_block_hash) !== decoded.refBlockHash) mismatch.push('ref_block_hash');
    if (whole(r.expiration) !== (decoded.expiration ?? 0)) mismatch.push('expiration');
    if (whole(r.timestamp) !== (decoded.timestamp ?? 0)) mismatch.push('timestamp');
    if (whole(r.fee_limit) !== Number(decoded.feeLimit)) mismatch.push('fee_limit');

    if (!Array.isArray(r.contract) || r.contract.length !== 1) {
      mismatch.push(`contract — the JSON lists ${Array.isArray(r.contract) ? r.contract.length : 'no'} contract(s), the bytes one`);
    } else {
      const c = r.contract[0];
      if (keysOf(c, 'contract')) {
        if (c.type !== 'TriggerSmartContract') mismatch.push(`type ${c.type}`);
        if (whole(c.Permission_id) !== decoded.permissionId) mismatch.push('Permission_id');
        const p = c.parameter;
        if (keysOf(p, 'parameter')) {
          if (p.type_url !== decoded.typeUrl) mismatch.push('type_url');
          const v = p.value;
          if (keysOf(v, 'value')) {
            if (address(v.owner_address) !== decoded.owner) mismatch.push('owner_address');
            if (address(v.contract_address) !== decoded.contract) mismatch.push('contract_address');
            if (typeof v.data !== 'string' || v.data.toLowerCase() !== decoded.calldata) mismatch.push('data');
            if (whole(v.call_value) !== 0) mismatch.push('call_value');
            if (whole(v.call_token_value) !== 0) mismatch.push('call_token_value');
            if (whole(v.token_id) !== 0) mismatch.push('token_id');
          }
        }
      }
    }
  }
  if (mismatch.length) {
    throw new Error(
      `the transaction's JSON disagrees with the bytes it would sign (${mismatch.join(', ')}) — this is forgery, not a glitch`,
    );
  }
}

/** The USDT contract on each network the desk uses — the only tokens the gate certifies. */
export const USDT_BY_NETWORK = Object.freeze({ mainnet: USDT_TRC20_MAINNET, nile: USDT_TRC20_NILE });

const EXPECTATIONS = ['network', 'token', 'owner', 'to', 'amount', 'permissionId', 'now', 'approvalTxid', 'deskWallets'];

/**
 * The gate the co-sign page runs before a wallet is handed anything
 * (cosign/src/handoff.ts) — once when the card is shown, and again at the tap.
 * For its first weeks this comment said the screen called it, and nothing did
 * (review of 4 October 2026, R2).
 *
 * WHAT THIS DOES AND DOES NOT PROVE — read before relying on it.
 *
 * It proves the bytes about to be signed are a transfer of the USDT contract
 * of the named network, from one of the named desk wallets, for the exact
 * amount and recipient the caller named, on the expected permission, within a
 * short window; that the txID matches, and is the one that was approved.
 *
 * It CANNOT prove the recipient is one you actually wanted paid. If the same
 * compromised server supplies both the transaction and the expectations, this
 * only proves the server agrees with itself. The recipient must therefore be
 * checked against something the server does not control: the operator reading
 * it off the Ledger screen, and a list of destinations the client keeps itself.
 *
 * Every expectation is MANDATORY, and an unknown one is refused: an omitted
 * field is a programming error, not permission to skip the check, and a field
 * this gate does not read must not look as if it were checked.
 */
export function assertCosignSafe(tx, expectations) {
  const fail = (m) => { throw new Error(m); };
  if (!tx || typeof tx !== 'object') fail('no transaction supplied');
  if (!expectations || typeof expectations !== 'object') fail('no expectations supplied — refusing to certify');
  for (const k of Object.keys(expectations)) {
    if (!EXPECTATIONS.includes(k)) fail(`expectation "${k}" is not one this gate checks — refusing to certify`);
  }

  const {
    network, token, owner, to, amount, permissionId, now, approvalTxid, deskWallets,
  } = expectations;

  // Mandatory-field validation. Absence must never read as satisfaction.
  if (!Object.hasOwn(USDT_BY_NETWORK, network ?? '')) fail('expectation "network" must be "mainnet" or "nile" — refusing to certify');
  if (typeof token !== 'string' || !token) fail('expectation "token" missing — refusing to certify');
  if (typeof owner !== 'string' || !owner) fail('expectation "owner" missing — refusing to certify');
  if (typeof to !== 'string' || !to) fail('expectation "to" missing — refusing to certify');
  if (typeof amount !== 'bigint') fail('expectation "amount" must be a bigint — refusing to certify');
  if (!Number.isSafeInteger(permissionId) || permissionId < 0) fail('expectation "permissionId" missing — refusing to certify');
  if (!Number.isSafeInteger(now) || now <= 0) fail('expectation "now" missing — supply the CLIENT clock, not a server value');
  if (typeof approvalTxid !== 'string' || !/^[0-9a-fA-F]{64}$/.test(approvalTxid)) {
    fail('expectation "approvalTxid" missing — the txID of the approved payout, 64 hex characters; refusing to certify');
  }
  if (!Array.isArray(deskWallets) || deskWallets.length === 0) {
    fail('expectation "deskWallets" missing — the desk wallets a payout may spend from; refusing to certify');
  }
  const desk = deskWallets.map((w, i) => {
    try {
      assertPlausibleAddress(w);
      return encodeTronAddress(decodeTronAddress(w));
    } catch {
      return fail(`expectation "deskWallets"[${i}] is not a valid TRON address — refusing to certify`);
    }
  });

  if (!tx.raw_data_hex) {
    fail('transaction has no raw_data_hex — cannot verify what would actually be signed');
  }

  const d = decodeSignedBytes(tx.raw_data_hex);

  // If the node also gave us a txID, it must match the bytes.
  if (tx.txID && String(tx.txID).toLowerCase() !== d.txID) {
    fail(`txID does not match raw_data_hex — the transaction object is inconsistent (forgery)`);
  }
  // Bind this certification to one specific approval, so the same approved
  // payout cannot be re-presented as a fresh transaction and signed again.
  if (approvalTxid.toLowerCase() !== d.txID) {
    fail('this is not the transaction you approved — txID differs from the queued approval');
  }

  assertJsonMatchesBytes(tx, d);

  // Token identity is pinned to a constant per network, never taken on trust
  // from the caller (A8): a caller-supplied "token" that is itself
  // attacker-chosen would let an arbitrary contract render as a USDT payout,
  // and the Nile contract is no USDT on mainnet.
  if (d.contract !== USDT_BY_NETWORK[network]) {
    fail(`token contract ${d.contract} is not a recognised USDT contract on ${network} — refusing`);
  }
  if (d.contract !== token) {
    fail(`token contract mismatch — bytes target ${d.contract}, card shows ${token}`);
  }
  if (!desk.includes(d.owner)) fail(`source ${d.owner} is not a known desk wallet — refusing`);
  if (d.owner !== owner) fail(`source wallet mismatch — bytes spend from ${d.owner}`);
  if (d.to !== to) fail(`recipient mismatch — bytes pay ${d.to}, card shows ${to}`);
  if (d.amount !== amount) fail(`amount mismatch — bytes send ${d.amount}, card shows ${amount}`);
  if (d.permissionId !== permissionId) fail(`permission mismatch — bytes use permission ${d.permissionId}`);

  // Expiration is mandatory and the window deliberately short, to stop a
  // signature being harvested now and broadcast much later.
  if (!d.expiration) fail('transaction has no expiration — refusing to sign an unbounded transaction');
  if (d.expiration <= now) fail('transaction has expired — rebuild and re-sign');
  if (d.expiration - now > MAX_COSIGN_WINDOW_MS) {
    fail(
      `expiration is ${Math.round((d.expiration - now) / 60000)} minutes out, beyond the ` +
      `${MAX_COSIGN_WINDOW_MS / 60000}-minute co-sign window — a long window enables signature harvesting`,
    );
  }

  return { ...d, checksPerformed: [
    'bytes-decoded', 'txid-verified', 'bound-to-approval', 'json-matches-bytes', 'token-pinned',
    'desk-wallet-pinned', 'owner-matched', 'recipient-matched', 'amount-matched', 'permission-matched',
    'expiration-bounded',
  ] };
}

// ---- expiration ----------------------------------------------------------

export const MAX_EXPIRATION_MS = 24 * 60 * 60 * 1000; // TRON protocol ceiling

/**
 * Extending expiration regenerates raw_data and therefore the txID, which
 * silently invalidates every signature already collected. This guard makes
 * that failure impossible to reach by accident.
 */
export function assertSafeToExtendExpiration(tx) {
  const sigs = tx?.signature;
  if (Array.isArray(sigs) && sigs.length > 0) {
    throw new Error(
      'cannot extend expiration after signatures exist — it changes the txID and voids them. ' +
      'Set the full window at build time, before the first signature.',
    );
  }
  return true;
}

export function assertExpirationWithinProtocolMax(expirationMs, buildTimeMs) {
  // NaN fails every comparison below, so an unchecked NaN would pass (A11).
  if (!Number.isSafeInteger(expirationMs) || !Number.isSafeInteger(buildTimeMs)) {
    throw new Error('expiration and build time must be whole epoch milliseconds');
  }
  const window = expirationMs - buildTimeMs;
  if (window <= 0) throw new Error('expiration is not in the future');
  if (window > MAX_EXPIRATION_MS) {
    throw new Error(
      `expiration window ${Math.round(window / 3600000)}h exceeds the 24h protocol maximum`,
    );
  }
  return true;
}

export { hexToBytes, bytesToHex };
